import { Routes, Route, Navigate } from "react-router-dom";
import { useSession } from "./lib/auth-client";
import { ThemeProvider } from "./hooks/use-theme";
import { ToastProvider } from "./components/shell/Toast";
import { usePageViews } from "./hooks/use-page-views";
import "./index.css";

import { LoginPage } from "./pages/auth/LoginPage";
import { RegisterPage } from "./pages/auth/RegisterPage";
import { LandingPage } from "./pages/LandingPage";
import { ReleasesMarketingPage } from "./pages/marketing/ReleasesMarketingPage";
import { FlagsMarketingPage } from "./pages/marketing/FlagsMarketingPage";
import { MocksMarketingPage } from "./pages/marketing/MocksMarketingPage";
import { WebhooksMarketingPage } from "./pages/marketing/WebhooksMarketingPage";
import { TeamsAndProjectsPage } from "./pages/TeamsAndProjectsPage";
import { ProjectOverviewPage } from "./pages/ProjectOverviewPage";
import { ReleasesListPage } from "./pages/ReleasesListPage";
import { ReleaseDetailPage } from "./pages/ReleaseDetailPage";
import { ReleaseSettingsPage } from "./pages/ReleaseSettingsPage";
import { ProjectSettingsPage } from "./pages/ProjectSettingsPage";
import { FeatureFlagsPage } from "./pages/FeatureFlagsPage";
import { ApiMocksPage } from "./pages/ApiMocksPage";
import { WebhooksPage } from "./pages/WebhooksPage";
import { WebhookDetailPage } from "./pages/WebhookDetailPage";
import { TeamSettingsPage } from "./pages/TeamSettingsPage";
import { DashboardLayout } from "./layouts/DashboardLayout";

function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { data: session, isPending } = useSession();

  if (isPending) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-muted-foreground">Loading…</div>
      </div>
    );
  }

  if (!session) {
    return <Navigate to="/login" replace />;
  }

  return <>{children}</>;
}

function PublicRoute({ children }: { children: React.ReactNode }) {
  const { data: session, isPending } = useSession();

  if (isPending) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-muted-foreground">Loading…</div>
      </div>
    );
  }

  if (session) {
    return <Navigate to="/teams" replace />;
  }

  return <>{children}</>;
}

function Analytics() {
  usePageViews();
  return null;
}

export function App() {
  return (
    <ThemeProvider>
      <ToastProvider>
      <Analytics />
      <Routes>
        <Route path="/" element={<LandingPage />} />
        <Route path="/releases" element={<ReleasesMarketingPage />} />
        <Route path="/flags" element={<FlagsMarketingPage />} />
        <Route path="/mocks" element={<MocksMarketingPage />} />
        <Route path="/webhooks" element={<WebhooksMarketingPage />} />
        <Route
          path="/login"
          element={
            <PublicRoute>
              <LoginPage />
            </PublicRoute>
          }
        />
        <Route
          path="/register"
          element={
            <PublicRoute>
              <RegisterPage />
            </PublicRoute>
          }
        />

        <Route
          element={
            <ProtectedRoute>
              <DashboardLayout />
            </ProtectedRoute>
          }
        >
          <Route path="/dashboard" element={<Navigate to="/teams" replace />} />
          <Route path="/teams" element={<TeamsAndProjectsPage />} />
          <Route path="/teams/:teamId" element={<TeamsAndProjectsPage />} />
          <Route path="/teams/:teamId/settings" element={<TeamSettingsPage />} />
          <Route
            path="/teams/:teamId/projects/:projectId"
            element={<ProjectOverviewPage />}
          />
          <Route
            path="/teams/:teamId/projects/:projectId/settings"
            element={<ProjectSettingsPage />}
          />
          <Route
            path="/teams/:teamId/projects/:projectId/releases"
            element={<ReleasesListPage />}
          />
          <Route
            path="/teams/:teamId/projects/:projectId/releases/settings"
            element={<ReleaseSettingsPage />}
          />
          <Route
            path="/teams/:teamId/projects/:projectId/releases/:releaseId"
            element={<ReleaseDetailPage />}
          />
          <Route
            path="/teams/:teamId/projects/:projectId/flags"
            element={<FeatureFlagsPage />}
          />
          <Route
            path="/teams/:teamId/projects/:projectId/mocks"
            element={<ApiMocksPage />}
          />
          <Route
            path="/teams/:teamId/projects/:projectId/webhooks"
            element={<WebhooksPage />}
          />
          <Route
            path="/teams/:teamId/projects/:projectId/webhooks/:webhookId"
            element={<WebhookDetailPage />}
          />
        </Route>

        <Route path="*" element={<Navigate to="/teams" replace />} />
      </Routes>
      </ToastProvider>
    </ThemeProvider>
  );
}

export default App;
